/**
 * La ficha de una orden: donde esta el equipo y que se hace con el AHORA.
 *
 * VIVE ADENTRO DE UN DIALOGO, NO PARTIENDO LA PANTALLA
 * ----------------------------------------------------
 * Antes la ficha se abria al lado de la lista y las dos se repartian el ancho.
 * A 1280px eso deja 600px por columna; el encabezado de la ficha (titulo +
 * cuatro botones) no entra en 600 y el titulo se comia a si mismo. Ahora la
 * orden se abre en `DialogOrden` y este archivo aporta las tres piezas que ese
 * dialogo compone: el cuerpo (`CuerpoOrden`), las acciones (`AccionesDeLaOrden`,
 * que van al pie) y la linea de la relacion (`ClienteDeLaOrden`, que va al
 * encabezado).
 *
 * EL FLUJO REAL, Y NADA MAS
 * -------------------------
 * Recibir, cargar el costo y el presupuesto cuando el tecnico contesta, entregar y cobrar.
 * Tres momentos. La base sigue teniendo nueve estados validados, pero la ficha
 * no los recorre: en el pie va UN boton con lo unico que corresponde hacer en
 * este momento (`accionPrincipal`), al lado hasta dos secundarias, y el resto
 * (imprimir, WhatsApp, anular) en un menu "Mas".
 *
 * TRES TARJETAS, NO SEIS, Y NINGUNA ADENTRO DE OTRA
 * -------------------------------------------------
 * La plata, el equipo y el historial. Lo que antes era una tarjeta adentro de
 * otra (la seña, la falla declarada, el diagnostico, las observaciones) ahora
 * es un bloque separado por una linea: un borde gris adentro de otro borde gris
 * no agrega jerarquia, agrega ruido. "La entrega" desaparecio como tarjeta
 * porque la linea de tiempo ya cuenta lo mismo (comprobante y garantia incluidos).
 *
 * Lo que la base hace sola al entregar (facturar, descontar las señas,
 * calcular la garantia, borrar los accesos) esta escrito en el dialogo de
 * entrega ANTES de que pase, no despues.
 */
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { History, KeyRound, MoreHorizontal, Receipt, Smartphone, User } from "lucide-react";

import { formatear } from "@tora/shared";
import { ChipMoneda, Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { ItemWhatsapp } from "./BotonWhatsapp";
import { mensajeListo, mensajeSaldo } from "./mensajes";
import { aNumero, type EstadoOt } from "@/lib/tipos";
import { cn, formatearFecha } from "@/lib/utils";

import { AccesosDelEquipo } from "./AccesosDelEquipo";
import {
  ETIQUETA_ESTADO,
  accionPrincipal,
  avisoDeLaFactura,
  cobroAlVolver,
  esInterna,
  esTransicionValida,
  estaFacturada,
  facturaAnulada,
  leerPlata,
  momentosDe,
  nombreCliente,
  nombreOrden,
  puedeDevolverSena,
  puedeSena,
  puedePagarTecnico,
  saldoDeLaOrden,
  diagnosticoPendiente,
  esTerminal,
  pagoAlTecnico,
  type OrdenTaller,
} from "./logica";

export interface FichaOrdenProps {
  orden: OrdenTaller;
  /** Para los mensajes de WhatsApp. Sin teléfono los botones no se dibujan. */
  telefono?: string | null;
  /** Qué porcentaje se pide para arrancar, de la configuración. */
  anticipoPct?: number;
  onCargarCostos: () => void;
  /** Abre el diálogo que le pasa el presupuesto al cliente por WhatsApp. */
  onPresupuesto: () => void;
  onSena: () => void;
  onPagarTecnico: () => void;
  /** Devolver lo cobrado de una anulada: anular no lo devuelve solo. */
  onDevolverSena?: () => void;
  onImprimirTalon: () => void;
  onEntregar: () => void;
  /** Sin arreglo o anular: los dos van por el dialogo de confirmacion. */
  onCambiarEstado: (estado: EstadoOt) => void;
  /** Las otras veces que vino este equipo. Ver `historialDelEquipo()`. */
  historial?: ReadonlyArray<OrdenTaller>;
  onAbrirOrden?: (id: number) => void;
}

/**
 * El cuerpo de la ficha: la frase de contexto y las tres tarjetas.
 *
 * El encabezado (badge, titulo, numero de orden) lo pone `DialogOrden`: ahi el
 * titulo de la ficha es el titulo del dialogo, y no puede haber dos.
 */
export function CuerpoOrden(props: FichaOrdenProps): JSX.Element {
  const { orden, historial = [], onAbrirOrden } = props;

  return (
    <div className="space-y-4">
      <QueSigue {...props} />

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <LaPlata orden={orden} />
        <ElEquipo orden={orden} />
      </div>

      <Historial orden={orden} anteriores={historial} onAbrirOrden={onAbrirOrden} />
    </div>
  );
}

/**
 * La relacion de la orden: de quien es el equipo. Va en el encabezado del
 * dialogo, debajo del numero.
 */
export function ClienteDeLaOrden({ orden }: { orden: OrdenTaller }): JSX.Element {
  if (esInterna(orden)) {
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <Smartphone className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="font-medium">Equipo propio</span>
        <span className="text-muted-foreground">·</span>
        {/* Un <a> y no un <Link />: la ficha se renderiza tambien sin router
            (tests), y abrir el producto es una vez cada tanto. */}
        <a
          href={`/catalogo-interno?id=${orden.producto_id}`}
          className="min-w-0 truncate underline-offset-2 hover:underline"
        >
          {orden.producto ?? orden.equipo}
        </a>
        {orden.producto_imei ? (
          <Codigo className="text-xs text-muted-foreground">{orden.producto_imei}</Codigo>
        ) : null}
      </p>
    );
  }

  if (orden.cliente_id === null || orden.cliente_id === undefined) {
    // Cerrada ya no se entrega: el aviso de «se puede entregar igual» quedaba
    // arriba de una orden anulada o entregada.
    if (esTerminal(orden.estado)) {
      return (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="size-4 shrink-0" aria-hidden="true" />
          Sin cliente
        </p>
      );
    }
    return (
      <Aviso tono="neutral" icono={User} compacto className="mt-1">
        Sin cliente asociado. Se puede entregar igual, pero se cobra entero en el momento: no hay
        cuenta corriente donde dejar diferencia.
      </Aviso>
    );
  }

  return (
    <p className="flex flex-wrap items-center gap-2 text-sm">
      <User className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <Link
        to="/clientes"
        search={{ id: orden.cliente_id }}
        className="min-w-0 truncate font-medium underline-offset-2 hover:underline"
      >
        {nombreCliente(orden)}
      </Link>
    </p>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las acciones, en el pie del dialogo
// ─────────────────────────────────────────────────────────────────────────────

interface Accion {
  clave: string;
  etiqueta: string;
  onClick: () => void;
}

/**
 * Una primaria (lo que sigue) a la derecha; a la izquierda, hasta dos
 * secundarias y un menu "Mas" con el resto.
 *
 * Devuelve un fragmento de dos hijos para que el `DialogFooter` los separe: el
 * grupo de la izquierda lleva `sm:mr-auto` y la primaria queda pegada al borde.
 * Nada de esto es `shrink-0`: era justamente eso lo que, en el panel partido,
 * hacia que los botones le robaran el ancho entero al titulo.
 *
 * Las secundarias se eligen por prioridad: corregir lo cargado va primero
 * porque es lo que se toca apenas se ve un numero mal; pagarle al tecnico es
 * plata que salio del local y que si no se asienta hace que la caja muestre de
 * mas; la seña cierra la lista. La que no entra va arriba del menu.
 *
 * Los estados intermedios (aprobado, en reparacion, listo) no tienen boton:
 * `entregar_orden` los recorre solo.
 */
export function AccionesDeLaOrden({
  orden,
  telefono,
  onCargarCostos,
  onPresupuesto,
  onSena,
  onPagarTecnico,
  onDevolverSena,
  onImprimirTalon,
  onEntregar,
  onCambiarEstado,
}: FichaOrdenProps): JSX.Element {
  const accion = accionPrincipal(orden);
  const interna = esInterna(orden);
  const sinArreglo = orden.estado === "SIN_ARREGLO";

  // El papel que se lleva el cliente: siempre disponible, se reimprime tantas
  // veces como haga falta.
  //
  // Con la orden cerrada eso solia ser lo unico que quedaba, y ahi estaba el
  // agujero: entregar NO le paga al tecnico (la base deja saldarlo despues a
  // proposito, `pagar_tecnico_ot` solo bloquea las anuladas y las ya pagadas),
  // asi que una entregada podia seguir debiendo plata sin un solo boton en
  // toda la app para saldarla. Mientras quede esa deuda es lo unico que falta
  // hacer con la orden: va de primaria.
  if (accion === "CERRADA") {
    // La anulada con plata cobrada: devolverla es un paso explicito (anular
    // no la devuelve sola). Antes no habia boton y el egreso se cargaba suelto
    // en la caja, sin la orden y sin tocar la cuenta del cliente.
    if (onDevolverSena && puedeDevolverSena(orden)) {
      return (
        <>
          <div className="flex flex-wrap items-center gap-2 sm:mr-auto">
            <Button type="button" variant="outline" onClick={onImprimirTalon}>
              Imprimir el talón
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={onDevolverSena}
            className="max-sm:w-full"
          >
            Devolver la seña
          </Button>
        </>
      );
    }
    if (!puedePagarTecnico(orden)) {
      return (
        <Button type="button" variant="outline" onClick={onImprimirTalon}>
          Imprimir el talón
        </Button>
      );
    }
    return (
      <>
        <div className="flex flex-wrap items-center gap-2 sm:mr-auto">
          <Button type="button" variant="outline" onClick={onImprimirTalon}>
            Imprimir el talón
          </Button>
        </div>
        <Button type="button" onClick={onPagarTecnico} className="max-sm:w-full">
          Pagar al técnico
        </Button>
      </>
    );
  }

  const secundarias: Accion[] = [];
  if (accion === "ENTREGAR" && !sinArreglo) {
    secundarias.push({
      clave: "corregir",
      etiqueta: interna ? "Corregir el costo" : "Corregir el presupuesto",
      onClick: onCargarCostos,
    });
  }
  if (puedePagarTecnico(orden)) {
    secundarias.push({ clave: "pagar", etiqueta: "Pagar al técnico", onClick: onPagarTecnico });
  }
  if (puedeSena(orden)) {
    secundarias.push({ clave: "sena", etiqueta: "Dejar seña", onClick: onSena });
  }
  const visibles = secundarias.slice(0, 2);
  const alMenu = secundarias.slice(2);

  // Los tres momentos en que se le escribe al cliente. Aparece el que
  // corresponde al estado de la orden, no los tres juntos.
  const whatsappPresupuesto = !interna && orden.costos_at && !estaFacturada(orden);
  const whatsappListo = !interna && orden.estado === "LISTO";
  const puedeAnular = esTransicionValida(orden.estado, "ANULADO");

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 sm:mr-auto">
        {visibles.map((s) => (
          <Button key={s.clave} type="button" variant="outline" onClick={s.onClick}>
            {s.etiqueta}
          </Button>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton icono={MoreHorizontal} variant="outline" aria-label="Más acciones" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {alMenu.map((s) => (
              <DropdownMenuItem key={s.clave} onSelect={s.onClick}>
                {s.etiqueta}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onSelect={onImprimirTalon}>Imprimir el talón</DropdownMenuItem>
            {whatsappPresupuesto ? (
              <DropdownMenuItem onSelect={onPresupuesto}>Pasar el presupuesto</DropdownMenuItem>
            ) : null}
            {whatsappListo ? (
              <ItemWhatsapp
                telefono={telefono}
                texto={
                  leerPlata(orden).senas < (leerPlata(orden).precio ?? 0)
                    ? mensajeSaldo(orden)
                    : mensajeListo(orden)
                }
                etiqueta="Avisar que está listo"
              />
            ) : null}
            {puedeAnular ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => onCambiarEstado("ANULADO")}
                >
                  Anular la orden
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {accion === "CARGAR_COSTOS" ? (
        <Button type="button" onClick={onCargarCostos} className="max-sm:w-full">
          {interna ? "Cargar el costo" : "Cargar costo y presupuesto"}
        </Button>
      ) : (
        <Button type="button" onClick={onEntregar} className="max-sm:w-full">
          {interna ? "Volvió al stock" : sinArreglo ? "Entregar" : "Entregar y cobrar"}
        </Button>
      )}
    </>
  );
}

/**
 * El parrafo de contexto: que va a pasar con esta orden y que hace la base
 * cuando pase. Los dos cierres a mano (volvio sin arreglo, el cliente no lo
 * aprobo) van aca abajo, como la alternativa a lo que sigue: son la salida de
 * la frase, no una accion mas del pie.
 *
 * Va en `muted` y no en una tarjeta normal a proposito: es la unica caja del
 * cuerpo que explica en vez de mostrar datos, y tiene que leerse distinto de
 * las tres que traen numeros.
 */
function QueSigue({ orden, onCambiarEstado }: FichaOrdenProps): JSX.Element {
  const accion = accionPrincipal(orden);

  if (accion === "CERRADA") {
    // Cerrada para el cliente no es cerrada para la caja: si el egreso del
    // tecnico nunca se asento, la orden todavia debe plata y hay que decirlo
    // aca, que es donde se mira.
    const plataCerrada = leerPlata(orden);
    const saldoCerrada = saldoDeLaOrden(orden);
    return (
      <Card as="section" variant="dashed" padding="normal" className="space-y-1">
        <p className="text-sm text-muted-foreground">
          {orden.estado === "ENTREGADO"
            ? // La interna no se entrega a nadie: vuelve al stock.
              `${esInterna(orden) ? "Volvió al stock" : "El equipo se entregó"}${orden.fecha_entrega ? ` el ${formatearFecha(orden.fecha_entrega)}` : ""}. La orden está cerrada.`
            : "La orden está anulada. No se puede reabrir."}
        </p>
        {/* Anular no devuelve la seña: se dice aca, con el numero, para que
            nadie crea que la caja se acomodo sola. */}
        {saldoCerrada.anulada && saldoCerrada.cobrado > 0 ? (
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-sm">
            <span>Quedó cobrado</span>
            <Importe
              monto={saldoCerrada.cobrado}
              moneda={saldoCerrada.moneda}
              tamano="sm"
              codigo={false}
            />
            <span className="text-muted-foreground">
              · anular no lo devolvió. Si corresponde, «Devolver la seña».
            </span>
          </p>
        ) : null}
        {puedePagarTecnico(orden) ? (
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-sm">
            <span>Falta pagarle al técnico</span>
            <Importe
              monto={plataCerrada.costoTecnico}
              moneda={plataCerrada.moneda}
              tamano="sm"
              codigo={false}
            />
            <span className="text-muted-foreground">
              · entregar no lo paga, el egreso se asienta acá.
            </span>
          </p>
        ) : null}
      </Card>
    );
  }

  const sinArreglo = orden.estado === "SIN_ARREGLO";
  const rechazado = orden.estado === "RECHAZADO";
  const interna = esInterna(orden);
  const cierreSinArreglo = esTransicionValida(orden.estado, "SIN_ARREGLO");
  const cierreRechazado = !interna && esTransicionValida(orden.estado, "RECHAZADO");

  return (
    <Card as="section" variant="muted" padding="normal" className="space-y-2">
      <TituloSeccion nivel="etiqueta">Qué sigue</TituloSeccion>
      <p className="text-sm">
        {accion === "CARGAR_COSTOS"
          ? interna
            ? "Falta lo que cobra el técnico. Cuando conteste, se carga y queda cuánto suma al costo del equipo."
            : "Falta el número del técnico. Cuando conteste, se cargan los costos y queda el precio."
          : sinArreglo || rechazado
            ? interna
              ? "Volvió sin arreglo. Se cierra la orden y el costo del equipo no cambia."
              : // Lo que se cobra depende del diagnostico: decia «sin cobrar»
                // tambien cuando el diagnostico estaba pendiente y la entrega
                // lo pedia.
                `${rechazado ? "El cliente no lo aprobó." : "Volvió sin arreglo."} Se devuelve al cliente. ${cobroAlVolver(orden)}`
            : interna
              ? "Cuando vuelva del técnico, se cierra la orden y el costo pasa al equipo."
              : "Con los costos cargados, lo que queda es entregar y cobrar."}
      </p>

      {cierreSinArreglo || cierreRechazado ? (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-xs text-muted-foreground">O si no terminó así:</span>
          {cierreSinArreglo ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCambiarEstado("SIN_ARREGLO")}
            >
              No tiene arreglo
            </Button>
          ) : null}
          {cierreRechazado ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCambiarEstado("RECHAZADO")}
            >
              No lo aprobó
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las secciones
// ─────────────────────────────────────────────────────────────────────────────

/**
 * La plata: primero el numero que se contesta por telefono ("¿cuánto sale?"),
 * despues de donde sale.
 *
 * Antes el precio estaba al final de una lista de tres lineas iguales y habia
 * que leerla entera para encontrarlo. Ahora va arriba y en grande, y el
 * desglose queda abajo como lo que es: la explicacion.
 *
 * Todo en la moneda de los costos, que el chip del titulo declara: por eso las
 * lineas van sin chip. La seña se muestra al cambio del dia que entro, que es
 * como la congelo la caja.
 */
function LaPlata({ orden }: { orden: OrdenTaller }): JSX.Element {
  const plata = leerPlata(orden);
  const interna = esInterna(orden);
  const facturado = aNumero(orden.facturado);
  const monedaFacturado = orden.moneda_facturado ?? plata.moneda;
  const anulada = facturaAnulada(orden);
  const aviso = avisoDeLaFactura(orden);
  // Lo cobrado contra lo que corresponde segun como termino la orden: el
  // precio, el diagnostico si volvio sin reparar, nada si se anulo.
  const saldo = saldoDeLaOrden(orden);
  // Sin seña y sin factura, "falta cobrar" seria el precio otra vez, dos
  // lineas mas abajo: el bloque no aparece.
  const hayCobro = !interna && (saldo.cobrado > 0 || saldo.devuelto > 0 || plata.facturado);
  // Volvio sin reparar: el precio es un presupuesto que no se hizo, y la
  // ganancia no se gano. Decia «Precio al cliente» y «Ganancia» igual.
  const noSeHizo = !interna && Boolean(orden.motivo_cierre);
  const diagPendiente = diagnosticoPendiente(orden);
  // Ya pagado, el técnico y la ganancia salen de lo que salió de la caja, no de
  // lo cotizado. En la interna no: ahí la suma es el costo que se le carga al
  // equipo, y tiene que dar el número de arriba.
  const pagadoTecnico = interna ? null : pagoAlTecnico(orden);
  const cotizadoDistinto =
    pagadoTecnico !== null && Math.abs(pagadoTecnico - plata.costoTecnico) > 0.009;
  const gananciaVista =
    pagadoTecnico === null
      ? plata.ganancia
      : Math.round((plata.ganancia + plata.costoTecnico - pagadoTecnico) * 100) / 100;

  return (
    <Card as="section" padding="normal" className="space-y-3">
      <TituloSeccion nivel="etiqueta" acciones={<ChipMoneda moneda={plata.moneda} />}>
        {interna ? "El costo" : "La plata"}
      </TituloSeccion>

      {plata.precio === null ? (
        <p className="text-sm text-muted-foreground">
          {/* Cerrada, el tecnico ya no va a contestar. */}
          {esTerminal(orden.estado)
            ? "Se cerró sin presupuesto."
            : interna
              ? "Todavía sin costo. Se carga cuando el técnico contesta."
              : "Todavía sin precio. Se arma cuando el técnico contesta."}
        </p>
      ) : (
        <>
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">
              {interna
                ? "Suma al costo del equipo"
                : noSeHizo
                  ? orden.motivo_cierre === "RECHAZADO"
                    ? "Presupuesto que no aprobó"
                    : "Presupuesto, no se hizo"
                  : "Precio al cliente"}
            </p>
            <Importe
              monto={plata.precio}
              moneda={plata.moneda}
              codigo={false}
              tamano="xl"
              className="font-semibold"
            />
          </div>

          <dl className="space-y-1.5 border-t border-border pt-3 text-sm">
            <Linea termino={pagadoTecnico !== null ? "Técnico, pagado" : "Técnico"} suave>
              <span className="inline-flex flex-wrap items-baseline justify-end gap-x-1.5">
                {cotizadoDistinto ? (
                  <span className="text-xs text-muted-foreground">
                    cotizado {formatear(plata.costoTecnico, plata.moneda)} ·
                  </span>
                ) : null}
                <Importe
                  monto={pagadoTecnico ?? plata.costoTecnico}
                  moneda={plata.moneda}
                  codigo={false}
                  tamano="sm"
                />
              </span>
            </Linea>
            {!interna && !noSeHizo ? (
              <Linea termino="Ganancia" suave>
                <Importe monto={gananciaVista} moneda={plata.moneda} codigo={false} tamano="sm" />
              </Linea>
            ) : null}
            {/* Tambien en las de clientes: sin la encomienda, tecnico mas
                ganancia no daba el precio de arriba. */}
            {plata.encomienda > 0 ? (
              <Linea termino="Encomienda" suave>
                <Importe
                  monto={plata.encomienda}
                  moneda={plata.moneda}
                  codigo={false}
                  tamano="sm"
                />
              </Linea>
            ) : null}
          </dl>
        </>
      )}

      {/* El diagnostico que quedo para pagar al retirar, con su motivo: no
          figuraba en ningun lado de la ficha. */}
      {diagPendiente ? (
        <p className="flex flex-wrap items-baseline gap-x-1.5 border-t border-border pt-3 text-sm">
          <span>Diagnóstico pendiente</span>
          <Importe
            monto={diagPendiente.falta}
            moneda={diagPendiente.moneda}
            tamano="sm"
            codigo={diagPendiente.moneda !== plata.moneda}
          />
          <span className="text-muted-foreground">· {diagPendiente.motivo}</span>
        </p>
      ) : null}

      {hayCobro ? (
        <dl className="space-y-1.5 border-t border-border pt-3 text-sm">
          {/* Lo cobrado antes de facturar: señas y diagnostico. Se llamaba
              «Seña» y, una vez facturada, sumaba tambien el pago de la
              entrega; facturada, lo cobrado lo dice el comprobante. */}
          {saldo.devuelto > 0 ? (
            <Linea termino="Devuelto al cliente">
              <Importe
                monto={saldo.devuelto}
                moneda={saldo.moneda}
                codigo={saldo.moneda !== plata.moneda}
              />
            </Linea>
          ) : null}
          {saldo.cobrado > 0 && !plata.facturado ? (
            <Linea termino={saldo.anulada ? "Quedó cobrado a cuenta" : "Cobrado a cuenta"}>
              <Importe
                monto={saldo.cobrado}
                moneda={saldo.moneda}
                codigo={saldo.moneda !== plata.moneda}
              />
            </Linea>
          ) : null}
          {plata.facturado ? (
            <>
              <Linea termino="Facturado">
                <Codigo className="text-xs">{orden.comprobante ?? "—"}</Codigo>
              </Linea>
              {plata.pagado !== null && !anulada ? (
                <Linea termino={facturado - plata.pagado > 0.009 ? "Queda en cuenta" : "Cobrado"}>
                  <Importe
                    monto={
                      facturado - plata.pagado > 0.009 ? facturado - plata.pagado : plata.pagado
                    }
                    moneda={monedaFacturado}
                    codigo={monedaFacturado !== plata.moneda}
                  />
                </Linea>
              ) : null}
            </>
          ) : saldo.falta !== null && saldo.aFavor === 0 ? (
            // Decia precio menos señas en cualquier estado: una sin arreglo
            // pedia el resto de una reparacion que no se hizo, y una anulada
            // el precio entero.
            <Linea termino="Falta cobrar">
              <Importe
                monto={saldo.falta}
                moneda={saldo.moneda}
                codigo={saldo.moneda !== plata.moneda}
              />
            </Linea>
          ) : null}
          {saldo.aFavor > 0 && !anulada ? (
            // La seña de lo que no se hizo no se usa en la factura: queda a
            // favor del cliente, y hay que avisarle o devolversela. Sin
            // cliente no hay cuenta donde dejarla: `entregar_orden` no asienta
            // nada y la plata queda en la caja; decia «A favor del cliente»
            // igual.
            <Linea
              termino={
                orden.cliente_id !== null && orden.cliente_id !== undefined
                  ? "A favor del cliente"
                  : "Cobrado de más, en la caja"
              }
            >
              <Importe
                monto={saldo.aFavor}
                moneda={saldo.moneda}
                codigo={saldo.moneda !== plata.moneda}
              />
            </Linea>
          ) : null}
        </dl>
      ) : null}

      {aviso ? (
        <Aviso tono="warning" compacto>
          {aviso}
        </Aviso>
      ) : null}

      {plata.facturado && !anulada ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Receipt className="size-3 shrink-0" aria-hidden="true" />
          Ya está en el circuito de cobro, como cualquier venta.
        </p>
      ) : null}
    </Card>
  );
}

function Linea({
  termino,
  suave = false,
  children,
}: {
  termino: string;
  suave?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={cn("text-muted-foreground", suave && "text-xs")}>{termino}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * El equipo: los datos del talon arriba y, separado por una linea, lo que se
 * dijo de el (la falla, el diagnostico, las observaciones).
 *
 * Los tres textos eran tres tarjetas grises adentro de esta tarjeta. Son el
 * mismo tipo de cosa y se leen seguidos: alcanza con la etiqueta chica arriba
 * de cada uno.
 */
function ElEquipo({ orden }: { orden: OrdenTaller }): JSX.Element {
  return (
    <Card as="section" padding="normal" className="space-y-3">
      <TituloSeccion nivel="etiqueta" icono={Smartphone}>
        El equipo
      </TituloSeccion>

      <dl className="space-y-2 text-sm">
        <Dato termino="Equipo" valor={orden.equipo} />
        {orden.modelo ? <Dato termino="Modelo" valor={orden.modelo} /> : null}
        {orden.color ? <Dato termino="Color" valor={orden.color} /> : null}
        {orden.imei ? <Dato termino="IMEI" valor={orden.imei} codigo /> : null}
        {orden.accesorios ? <Dato termino="Deja" valor={orden.accesorios} /> : null}
        {orden.tercerizado ? <Dato termino="Servicio técnico" valor={orden.tercerizado} /> : null}
        {orden.transporte ? <Dato termino="Transporte" valor={orden.transporte} /> : null}
      </dl>

      {orden.tiene_accesos && orden.estado !== "ENTREGADO" ? (
        <AccesosDelEquipo ordenId={orden.id} />
      ) : null}

      <div className="space-y-3 border-t border-border pt-3">
        {/* La interna no tiene cliente: la falla la anota el local, y es lo
            que hay que hacerle al equipo. */}
        <Texto etiqueta={esInterna(orden) ? "Qué hay que hacerle" : "Lo que dijo el cliente"}>
          {orden.falla_declarada}
        </Texto>
        {orden.diagnostico ? (
          <Texto etiqueta="Lo que encontró el técnico">{orden.diagnostico}</Texto>
        ) : null}
        {orden.observaciones ? <Texto etiqueta="Observaciones">{orden.observaciones}</Texto> : null}
      </div>

      {orden.estado === "ENTREGADO" ? (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <KeyRound className="mt-px size-3 shrink-0" aria-hidden="true" />
          <span>
            {esInterna(orden)
              ? "La cuenta, la clave y el patrón del equipo se borraron al cerrar la orden. Eran para probarlo en el taller."
              : "La cuenta, la clave y el patrón del equipo se borraron al entregarlo. Eran para probarlo; una vez que se fue con el cliente no hay razón para conservarlos."}
          </span>
        </p>
      ) : null}
    </Card>
  );
}

function Texto({ etiqueta, children }: { etiqueta: string; children: string }): JSX.Element {
  return (
    <div className="space-y-0.5">
      <span className="text-xs text-muted-foreground">{etiqueta}</span>
      <p className="whitespace-pre-line text-sm">{children}</p>
    </div>
  );
}

/**
 * La historia del equipo: que paso con esta orden, y las otras veces que vino.
 *
 * La linea de tiempo usa las fechas que la vista trae (ingreso, costos,
 * entrega) y las palabras del flujo real; el ultimo momento se lleva el
 * comprobante y la garantia, asi que no hace falta una tarjeta "La entrega"
 * que repita lo mismo dos bloques mas abajo. Las visitas anteriores se buscan
 * por IMEI entre las ordenes cargadas en pantalla; un telefono que volvio tres
 * veces se ve de un vistazo, con lo que se hizo y lo que se cobro cada vez.
 */
function Historial({
  orden,
  anteriores,
  onAbrirOrden,
}: {
  orden: OrdenTaller;
  anteriores: ReadonlyArray<OrdenTaller>;
  onAbrirOrden?: (id: number) => void;
}): JSX.Element {
  const momentos = momentosDe(orden);

  return (
    <Card as="section" padding="normal" className="space-y-3">
      <TituloSeccion nivel="etiqueta" icono={History}>
        Historial
      </TituloSeccion>

      <ol className="space-y-2">
        {momentos.map((m, i) => (
          <li key={m.clave} className="flex gap-3 text-sm">
            <span className="flex flex-col items-center">
              <span
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  i === momentos.length - 1 ? "bg-primary" : "bg-border",
                )}
                aria-hidden="true"
              />
              {i < momentos.length - 1 ? (
                <span className="w-px flex-1 bg-border" aria-hidden="true" />
              ) : null}
            </span>
            <span className="min-w-0 flex-1 pb-1">
              <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="font-medium">{m.titulo}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatearFecha(m.fecha)}
                </span>
              </span>
              {m.detalle ? (
                <span className="block text-xs text-muted-foreground">{m.detalle}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>

      {anteriores.length > 0 ? (
        <div className="space-y-1.5 border-t border-border pt-3">
          <span className="text-xs text-muted-foreground">
            Este equipo ya vino {anteriores.length === 1 ? "una vez" : `${anteriores.length} veces`}
          </span>
          <ul className="space-y-1">
            {anteriores.map((o) => {
              const plata = leerPlata(o);
              return (
                <li key={o.id}>
                  {/* Un ghost de dos lineas: el alto fijo del boton se suelta
                      para que entren el numero y el diagnostico. */}
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onAbrirOrden?.(o.id)}
                    disabled={!onAbrirOrden}
                    className="h-auto w-full items-baseline justify-between gap-3 px-2 py-1.5 text-left font-normal disabled:cursor-default"
                  >
                    <span className="min-w-0">
                      <span className="block truncate">
                        <Codigo className="text-xs text-muted-foreground">{nombreOrden(o)}</Codigo>{" "}
                        · {o.falla_declarada}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {formatearFecha(o.fecha_ingreso)} ·{" "}
                        {ETIQUETA_ESTADO[o.estado].toLowerCase()}
                        {o.diagnostico ? ` · ${o.diagnostico}` : ""}
                      </span>
                    </span>
                    {plata.precio !== null ? (
                      <Importe
                        monto={plata.precio}
                        moneda={plata.moneda}
                        tamano="sm"
                        className="shrink-0"
                      />
                    ) : null}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : orden.imei ? (
        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          Primera vez que viene este equipo.
        </p>
      ) : null}
    </Card>
  );
}

function Dato({
  termino,
  valor,
  codigo = false,
}: {
  termino: string;
  valor: string;
  codigo?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{termino}</dt>
      <dd className="min-w-0 truncate text-right">
        {codigo ? <Codigo className="text-xs">{valor}</Codigo> : valor}
      </dd>
    </div>
  );
}
